import { makeStyles } from '@material-ui/core/styles';
import { EntityGroupProfileCard } from '@backstage/plugin-org';

/**
 * The `en` label for `@backstage/plugin-org`'s
 * `groupProfileCard.editIconButtonTitle`, rendered as the `title` of the pencil
 * IconButton in `GroupProfileCard`'s header actions. It is not overridden in
 * `packages/app/src/translations/org.ts`, so the upstream default applies.
 * Pinned here because the CSS rule below matches on it — see
 * `GroupProfileCardWithoutEdit.test.tsx`, which fails if upstream renames it.
 */
export const EDIT_ICON_BUTTON_LABEL = 'Edit Metadata';

const useStyles = makeStyles({
  root: {
    // Preserves the equal-height stretching the `Grid item` previously gave
    // the card directly.
    height: '100%',
    // Two independent selectors for the same element, because upstream sets
    // *both* `title` and `aria-label` to that label on the pencil IconButton
    // (verified in `@backstage/plugin-org@0.7.7`'s `GroupProfileCard`, in both
    // of its branches). Either attribute alone is enough to hide it, so a
    // future upstream change that drops one — or a locale that only translates
    // one — still hides the action rather than silently exposing a bypass of
    // the permission-gated "Edit Team" / "Archive Team" flow.
    //
    // No tag qualifier, because the pencil's element varies: with
    // `backstage.io/edit-url` present it is an `<a>` (upstream passes
    // `component={Link}`); without the annotation it is a disabled `<button>`.
    //
    // Scoped to this wrapper only — does not affect UserProfileCard,
    // EntityUserHero or any other entity card. The sibling refresh IconButton
    // has `title="Schedule entity refresh"` and stays visible.
    [`& [title="${EDIT_ICON_BUTTON_LABEL}"]`]: {
      display: 'none',
    },
    // Prefix match (`^=`), not exact: in the `<a>` branch
    // `@backstage/core-components`' `Link` appends
    // `", Opens in a new window"` to the `aria-label` for external hrefs, so
    // the attribute is only *prefixed* by the label there. `title` is left
    // untouched by `Link`, hence the exact match above.
    [`& [aria-label^="${EDIT_ICON_BUTTON_LABEL}"]`]: {
      display: 'none',
    },
  },
});

/**
 * Hides the pencil (edit) IconButton that `@backstage/plugin-org`'s
 * `GroupProfileCard` always renders in its header — it links straight to the
 * team YAML on GitHub (`backstage.io/edit-url`), bypassing the
 * permission-gated "Edit Team" / "Archive Team" buttons in `TeamActions`,
 * which are the only authorized way to change a team.
 *
 * `EntityGroupProfileCard`'s props are only `{ showLinks? }` as of
 * `@backstage/plugin-org@0.7.7` — there is no prop to suppress the action, and
 * it is rendered unconditionally (merely `disabled` when the annotation is
 * absent), so a scoped CSS rule is the least invasive lever.
 */
export const GroupProfileCardWithoutEdit = (props: { showLinks?: boolean }) => {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <EntityGroupProfileCard {...props} />
    </div>
  );
};
